import _ from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useEventParticipants } from '../participant/useEventParticipants';

export const useDirectoryItem = (eventId, itemId) => useSelector((state) => state.events[eventId].directory.all[itemId]);

/**
 * Returns the contacts associated with a given directory item.
 *
 * The contacts are taken from the following sources (in order of preference):
 * - contacts field in the directory item
 * - participant query in the directory item (searches participant extra profile data)
 * - exhibitor_backrefs in the participant extra profile data (legacy from native app)
 *
 * @param {Number} eventId Event ID
 * @param {Number} itemId Directory item ID
 * @returns {Array} List of contacts (participant objects)
 */
export const useDirectoryItemContacts = (eventId, itemId) => {
    const item = useDirectoryItem(eventId, itemId),
        participants = useEventParticipants(eventId);

    return useMemo(
        () =>
            item?.contacts ??
            _([
                // Search by exhibitor_backrefs (not used on the native app; maybe can be removed...)
                ..._(participants)
                    .filter((p) => {
                        return _(p.extra_profile_data).find((epd) => {
                            return _(epd.exhibitor_backrefs).find((exhibitorId) => {
                                return exhibitorId == itemId;
                            });
                        });
                    })
                    .flatten()
                    .compact()
                    .value(),
                // Search by participant query
                ..._(item?.participant_query)
                    .map((value, field) => {
                        return _(participants)
                            .filter((p) => {
                                return (
                                    _(p.extra_profile_data)
                                        .filter((epd) => epd.name.toLowerCase() == field.toLowerCase())
                                        .find((epd) => _(epd.value).includes(value)) ||
                                    (field.toLowerCase() == 'company' && (p.company || '').toLowerCase().trim() == value.toLowerCase().trim())
                                );
                            })
                            .value();
                    })
                    .flatten()
                    .compact()
                    .value(),
            ])
                .uniqBy((p) => [p.id, p.participant_id].join())
                .value(),
        [item, participants]
    );
};
